import { zodResolver } from '@hookform/resolvers/zod';
import { PencilSimpleIcon } from '@phosphor-icons/react';
import { Skeleton } from 'antd';
import type { FocusEvent } from 'react';
import { useForm, useWatch, type Resolver } from 'react-hook-form';

import { UserAvatar } from '@/components/common/user-avatar';
import { RegionError } from '@/components/feedback/error-state';
import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextField } from '@/components/forms';
import { ApiError, UnauthorizedError } from '@/lib/api/errors';
import { ADMIN_AVATAR_SRC } from '@/lib/avatar';
import { dedupeMessages, type ValidationDetail } from '@/lib/validation/server-details';
import { useAuth } from '@/providers/auth-provider';

import { DEALER_PROFILE_COPY, PROFILE_COPY, PROFILE_ERROR_CODES } from '../constants';
import { useIsDealer, useProfile, useUpdateProfile } from '../hooks';
import {
  dealerProfileFormSchema,
  isUnchangedProfile,
  profileFormSchema,
  toFormValues,
  type ProfileFormValues,
} from '../schemas';
import { useProfileModalStore } from '../use-profile-modal-store';

import styles from './edit-profile-modal.module.scss';
import fields from './profile-fields.module.scss';

const COPY = PROFILE_COPY.editProfile;
const EDITABLE = ['firstName', 'lastName', 'email'] as const;
type Editable = (typeof EDITABLE)[number];
const isEditable = (name: string): name is Editable =>
  (EDITABLE as readonly string[]).includes(name);
const stripLeading = (raw: string) => raw.replace(/^\s+/, '');

export function EditProfileModal({ onClose }: { onClose: () => void }) {
  const profile = useProfile();
  const update = useUpdateProfile();
  const setOpen = useProfileModalStore((s) => s.setOpen);
  const { user, rememberMe, setSession } = useAuth();
  const toast = useToast();
  const isDealer = useIsDealer();
  const schema = isDealer ? dealerProfileFormSchema : profileFormSchema;
  const form = useForm<ProfileFormValues>({
    // One form type over both schemas: the shared `<TextField>`/`<FormModal>` take a single-generic
    // `Control<T>`, so the input/output split (email '' in, null out) can't ride RHF's 3rd generic.
    resolver: zodResolver(schema) as Resolver<ProfileFormValues>,
    mode: 'onTouched', // errors on blur (§5a/§6a); isValid still tracks every keystroke (§4a)
    values: profile.data && toFormValues(profile.data),
  });
  const { control } = form;
  // 122131 AC6: a Dealer's Save also waits for a change. Judged on the parsed values (trimmed, email
  // lower-cased, blank ≡ null) — the same rule `onSubmit` applies — so case or spacing is no change.
  const watched = useWatch({ control });
  const parsed = isDealer ? dealerProfileFormSchema.safeParse(watched) : undefined;
  const dealerUnchanged =
    !!parsed?.success && !!profile.data && isUnchangedProfile(parsed.data, profile.data);

  // §5c/§6e: the value itself is trimmed on blur, not just validated trimmed. `<TextField>` gives
  // each input `id={name}`; React's onBlur bubbles, so one listener covers all three.
  const trimOnBlur = (e: FocusEvent<HTMLDivElement>) => {
    const name = e.target.id;
    if (!isEditable(name)) return;
    const value = form.getValues(name) ?? '';
    if (value !== value.trim()) form.setValue(name, value.trim(), { shouldValidate: true });
  };

  const onSubmit = async (values: ProfileFormValues) => {
    if (!profile.data || isUnchangedProfile(values, profile.data)) return onClose(); // §10
    try {
      await update.mutateAsync(values);
    } catch (error) {
      if (error instanceof UnauthorizedError) return; // sessionExpiry owns it (§14)
      if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.emailAlreadyExists) {
        form.setError('email', {
          message: isDealer ? DEALER_PROFILE_COPY.emailTaken : COPY.emailTaken,
        });
        return;
      }
      const details =
        error instanceof ApiError &&
        error.code === PROFILE_ERROR_CODES.validationFailed &&
        Array.isArray(error.details)
          ? (error.details as ValidationDetail[])
          : [];
      const placed = details.filter((d): d is ValidationDetail & { field: Editable } =>
        isEditable(d.field),
      );
      placed.forEach((d) => form.setError(d.field, { message: d.message }));
      const unplaced = details.filter((d) => !isEditable(d.field));
      // ADR-0008: a reason the server gave but no input can carry is toasted verbatim; with no
      // details at all (503, network) the AC's generic copy stands (§12a).
      if (unplaced.length > 0 || details.length === 0) {
        toast.error({
          title:
            unplaced.length > 0 ? dedupeMessages(unplaced).join(' ') : PROFILE_COPY.genericError,
          key: 'edit-profile-error',
        });
      }
      return; // §12a: modal and values stay
    }
    // §9b: the chip reads the session user, which `<AuthProvider>` loads once at boot rather than
    // through a query — invalidating `authKeys.me` cannot reach it, so the saved names go in directly.
    if (user)
      setSession({ ...user, firstName: values.firstName, lastName: values.lastName }, rememberMe);
    onClose();
    toast.success({ title: COPY.saved });
  };

  return (
    <FormModal
      open
      form={form}
      onSubmit={onSubmit}
      onClose={onClose}
      title={COPY.title}
      size="lg"
      className={styles.modal}
      maskClosable // §3f/§11: silent discard — overrides FormModal's dirty-guard
      // Internal Users keep the shipped "valid & unchanged closes" (§4b).
      primaryDisabled={!profile.data || !form.formState.isValid || dealerUnchanged}
    >
      {profile.isPending ? (
        <Skeleton active avatar={{ size: 94 }} paragraph={{ rows: 4 }} />
      ) : profile.isError ? (
        <RegionError message={COPY.loadError} onRetry={() => void profile.refetch()} />
      ) : (
        <div className={styles.body} onBlur={trimOnBlur}>
          <UserAvatar
            name={`${profile.data.firstName} ${profile.data.lastName}`}
            src={ADMIN_AVATAR_SRC}
            size={94}
            className={styles.avatar}
          />
          <div className={styles.grid}>
            <TextField
              name="firstName"
              control={control}
              label="First Name"
              required
              maxLength={50}
              transform={stripLeading}
            />
            <TextField
              name="lastName"
              control={control}
              label="Last Name"
              required
              maxLength={50}
              transform={stripLeading}
            />
            <TextField
              name="email"
              control={control}
              label="Email"
              required={!isDealer}
              maxLength={100}
            />
            <TextField
              name="phone"
              control={control}
              label="Phone Number"
              required
              disabled
              prefix="+1"
              className={fields.readOnly}
              suffix={
                <button
                  type="button"
                  className={styles.pencil}
                  aria-label={COPY.changePhone}
                  onClick={() => setOpen('phone')} // §8a/§8b: replaces 'edit' → unmounts, discards
                >
                  <PencilSimpleIcon size={13} />
                </button>
              }
            />
            <TextField
              name="role"
              control={control}
              label="Role"
              disabled
              className={fields.readOnly}
            />
          </div>
        </div>
      )}
    </FormModal>
  );
}
